import { useState, useEffect, useCallback, useRef } from 'react';
import type { Product, CatalogFilters, PaginationMeta } from '../types/product';
import { fetchProducts, fetchAllProducts } from '../services/productsApi';
import { newestMerchandise } from '../utils/latestProducts';
import { hasStockType } from '../types/product';
import { API_CONFIG } from '../services/config';
import { loadSearchCatalog } from '../services/searchCatalog';
import { searchProducts, sortProducts } from '../utils/productSearch';

// ─── Paginated hook — for catalog page (server-side pagination) ───────────────
interface UsePaginatedProductsResult {
  products: Product[];
  pagination: PaginationMeta | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
  isLive: boolean;
}

/**
 * `newestFirst`: del más reciente al más viejo (productId desc). La API no sabe
 * ordenar así, de modo que se trae todo lo que coincide con los filtros, se ordena
 * y se pagina aquí. La lista completa se guarda por filtros: pasar de página no
 * vuelve a pedirla; refresh() sí. Con búsqueda (`globalFilter`) igual: la API es
 * demasiado estricta, así que se filtra aquí sobre esa misma lista.
 *
 * El catálogo es MERCANCÍA, igual que /novedades: los servicios («Instalación»,
 * «Mantenimiento», «Otros cargos») tienen su propia página y nunca se listan aquí.
 */
export function usePaginatedProducts(
  filters: CatalogFilters = {},
  { newestFirst = false }: { newestFirst?: boolean } = {}
): UsePaginatedProductsResult {
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const newestFirstRef = useRef(newestFirst);
  newestFirstRef.current = newestFirst;
  const allCache = useRef<{ key: string; promise: Promise<Product[]> } | null>(null);
  // Solo la última carga pinta: con la búsqueda cada tecla carga, y una respuesta
  // lenta no debe pisar a la que llegó después.
  const loadSeq = useRef(0);

  const load = useCallback(async (silent = false, reload = false) => {
    const seq = ++loadSeq.current;
    const { page = 1, pageSize = API_CONFIG.DEFAULT_PAGE_SIZE, sorts, globalFilter, ...rest } = filtersRef.current;
    const query = globalFilter?.trim() ?? '';
    // La búsqueda se hace aquí (src/utils/productSearch.ts), no en la API
    const local = newestFirstRef.current || query !== '';
    const key = JSON.stringify(rest);
    const cached = local && !reload && allCache.current?.key === key;
    if (!silent && !cached) setLoading(true);
    setError(null);
    try {
      if (local) {
        if (!cached) {
          const promise = key === '{}'
            ? loadSearchCatalog(reload)
            : fetchAllProducts(rest).then(newestMerchandise);
          allCache.current = { key, promise };
          promise.catch(() => {
            if (allCache.current?.promise === promise) allCache.current = null;
          });
        }
        let all = await allCache.current!.promise;
        if (seq !== loadSeq.current) return;
        if (query) {
          all = searchProducts(all, query);
          // Con búsqueda, «Más recientes» (el orden por defecto) pasa a ser «más
          // relevantes»; un orden elegido (precio, nombre…) se respeta.
          if (!newestFirstRef.current) all = sortProducts(all, sorts);
        }
        const totalPages = Math.max(1, Math.ceil(all.length / pageSize));
        setProducts(all.slice((page - 1) * pageSize, page * pageSize));
        setPagination({
          current_page: page,
          per_page: pageSize,
          total_items: all.length,
          total_pages: totalPages,
          has_next: page < totalPages,
          has_prev: page > 1,
        });
      } else {
        const result = await fetchProducts(filtersRef.current);
        if (seq !== loadSeq.current) return;
        setProducts(result.data.filter((p) => hasStockType(p.productType)));
        setPagination(result.pagination);
      }
      setLastUpdated(new Date());
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setError('Error al cargar productos');
      console.error('[usePaginatedProducts]', err);
    } finally {
      if (!silent && seq === loadSeq.current) setLoading(false);
    }
  }, []);

  // Re-fetch whenever key filter values change
  useEffect(() => {
    load();
  }, [
    load,
    filters.page,
    filters.pageSize,
    filters.globalFilter,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(filters.sorts),
    newestFirst,
  ]);

  return {
    products,
    pagination,
    loading,
    error,
    refresh: () => load(false, true),
    lastUpdated,
    isLive: true,
  };
}

// ─── Flat hook — for home page sections (latest products, etc.) ───────────────
interface UseProductsResult {
  products: Product[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
  isLive: boolean;
}

export function useProducts(
  filters?: Omit<CatalogFilters, 'page' | 'pageSize'>
): UseProductsResult {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await fetchAllProducts(filtersRef.current);
      setProducts(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Error al cargar productos');
      console.error('[useProducts]', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (API_CONFIG.POLLING_INTERVAL > 0) {
      intervalRef.current = setInterval(() => load(true), API_CONFIG.POLLING_INTERVAL);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [load]);

  return {
    products,
    loading,
    error,
    refresh: () => load(),
    lastUpdated,
    isLive: true,
  };
}